import { Login } from '@/types/Login.type'
import { Member } from '@/types/Member.type'
import { MemberLead } from '@/types/MemberLead.type'
import SharedStorage from '@/utils/SharedStorage'
import * as Sentry from '@sentry/browser'
import { io } from 'socket.io-client'
import { ActionContext } from 'vuex'
import API from '../../services/API'
import { State } from './state'
import { DateTime } from 'luxon'
import { jwtTokenName, refreshJwtTokenName, refreshUserName, SpaName } from '@/utils/auth'

export async function getMe(ctx: ActionContext<State, any>) {
    if (ctx.state.me === null) {
        const decodedToken = ctx.state.decodedToken
        try {
            const me = ctx.getters['isLead'] ? decodedToken : await API.getMe()
            SharedStorage.set(refreshUserName, {
                ...(SharedStorage.get(refreshUserName) || {}),
                [SpaName.LECLUB]: false,
            })
            await ctx.dispatch('setMe', me)
            return me
        } catch (e) {
            // handle get me error catch it but dont need to do anything
        }
    }

    return ctx.state.me
}

export async function setMe(ctx: ActionContext<State, any>, me: Member | MemberLead) {
    await ctx.commit('SET_ME', { me })

    Sentry.configureScope(scope => {
        scope.setUser(me)
    })

    await ctx.commit('SET_ME_COMPLETED')
}

export async function setToken(
    ctx: ActionContext<State, any>,
    { token, refreshToken }: { token?: string | null; refreshToken?: string }
) {
    const cookieDuration = DateTime.now()
        .plus({ year: 1 })
        .toJSDate()

    API._setBearer(token)

    await ctx.commit('DECODE_TOKEN', { token })

    if (token) {
        SharedStorage.set(jwtTokenName, token, cookieDuration) // 24 hours
    } else {
        SharedStorage.delete(jwtTokenName)
    }

    if (refreshToken) {
        SharedStorage.set(refreshJwtTokenName, refreshToken, cookieDuration) // 24 hours
    } else {
        SharedStorage.delete(refreshJwtTokenName)
    }
}

export async function login(ctx: ActionContext<State, any>, data: Login & { context?: string }) {
    const { redirect, refresh_token, token } = await API.loginCheck(data)
    await ctx.dispatch('setToken', { token, refreshToken: refresh_token })
    await ctx.dispatch('getMe')

    return redirect
}

export async function logout(ctx: ActionContext<State, any>, targetUrl?: string) {
    await ctx.dispatch('setToken', { token: null, refreshToken: null })
    await ctx.dispatch('setMe', null)
    window.location.href = `/auth/force-logout${(targetUrl && `?targetUrl=${targetUrl}`) || ''}`
}

export async function getMembership(ctx: ActionContext<State, any>) {
    try {
        ctx.commit('SET_LOADING', { membership: true })
        const membership = await API.getMembershipDetails()
        ctx.commit('SET_MEMBERSHIP', { membership })
    } catch (e) {
        console.error({ e })
        //window.location.href = '/404'
    } finally {
        ctx.commit('SET_LOADING', { membership: false })
    }
}

export async function getSubscriptions(ctx: ActionContext<State, any>) {
    try {
        ctx.commit('SET_LOADING', { subscriptions: true })
        const subscriptions = await API.getSubscriptions()
        ctx.commit('SET_SUBSCRIPTIONS', { subscriptions })
    } catch (e) {
        console.error({ e })
        //window.location.href = '/404'
    } finally {
        ctx.commit('SET_LOADING', { subscriptions: false })
    }
}

type ConnectSocketParams = {
    url: string
}
/**
 * Connect to the socket only if we have a jwtToken
 */
export async function connectSocket(ctx: ActionContext<State, any>, { url }: ConnectSocketParams) {
    return new Promise((resolve, reject) => {
        const jwtToken: string | null = ctx.state.token

        if (!jwtToken) {
            reject(
                new Error(`No jwt token found: Not authorized to connect to the socket, ${url} with token ${jwtToken}`)
            )
        }

        const socket = io(url, {
            reconnection: true,
            transports: ['websocket'],
        })

        socket.on('connect', () => {
            ctx.commit('SET_SOCKET_ID', { socketId: socket.id })
            socket.emit('auth:login', jwtToken, () => {
                resolve(true)
            })
        })

        ctx.commit('SET_SOCKET', { socket })
    })
}

/**
 * Request a networking token for the connected user.
 */
export async function requestNetworkingToken(ctx: ActionContext<State, any>): Promise<string> {
    const { token } = await API.requestNetworkingAccessToken()

    ctx.commit('SET_NETWORKING_TOKEN', { token })
    window.sessionStorage.setItem('networkingToken', token)
    return token
}

type LoginToEventPayload = {
    eventId: string | number
    email?: string
    code: string
}

/**
 *  Lead login to an event. Request token JWT and connect the socket
 *  In case of "magic link" login:
 *      - don't pass an email
 *      - the code is a token (uuid)
 */
export async function loginToEvent(ctx: ActionContext<State, any>, payload: LoginToEventPayload): Promise<void> {
    const { jwtToken } = await API.loginLiveEvent(payload.eventId, payload.email, payload.code)

    await ctx.dispatch('setToken', { token: jwtToken })
    await API.forceLogin()

    ctx.dispatch('connectSocket', { url: process.env.VUE_APP_NETWORKING_QUEUE_URL })
}
